import React, { useState, useEffect } from 'react';
import axios from 'axios';

const ForecastModal = ({ 
  show, 
  onClose, 
  goalData, // { id, title, period, years, matrix, current_year, parent_goal_id }
  onForecastApplied 
}) => {
  const [forecastConfig, setForecastConfig] = useState({
    targetYear: null,
    growthMode: 'percent',
    growthPercent: 10,
    growthAbsolute: 0,
    growthType: 'continuous',
    hasMinValue: false,
    minValue: 0,
    hasMaxValue: false,
    maxValue: 100
  });
  
  // Anos base selecionados (pode ser editado pelo usuário)
  const [selectedBaseYears, setSelectedBaseYears] = useState([]);

  // Obter anos com resultados concluídos (todos disponíveis)
  const getYearsWithResults = (data) => {
    if (!data || !data.matrix) return [];
    return data.years.filter(year => {
      return data.matrix.some(row => 
        row.cells.some(cell => 
          cell.year === year && 
          cell.result !== null && 
          cell.result !== undefined &&
          cell.status === 'concluded'
        )
      );
    });
  };

  // Toggle de ano base
  const toggleBaseYear = (year) => {
    setSelectedBaseYears(prev => {
      if (prev.includes(year)) {
        // Não permitir remover o último ano
        if (prev.length <= 1) return prev;
        return prev.filter(y => y !== year);
      } else {
        return [...prev, year].sort((a, b) => a - b);
      }
    });
  };

  // Inicializar config quando modal abre
  useEffect(() => {
    if (show && goalData) {
      const yearsWithResults = getYearsWithResults(goalData);
      setSelectedBaseYears(yearsWithResults);
      
      // Definir ano destino padrão como o próximo ano
      const nextYear = goalData.current_year + 1;
      const defaultTargetYear = goalData.years.includes(nextYear) 
        ? nextYear 
        : goalData.years[goalData.years.length - 1] || goalData.current_year;
      
      let totalBase = 0;
      let monthsCount = 0;
      goalData.matrix.forEach(row => {
        yearsWithResults.forEach(year => {
          const cell = row.cells.find(c => c.year === year);
          if (cell && cell.result !== null && cell.result !== undefined && cell.status === 'concluded') {
            totalBase += cell.result;
            monthsCount++;
          }
        });
      });
      const avgMonthBase = monthsCount > 0 ? totalBase / monthsCount : 0;
      const avgYearTotal = avgMonthBase * 12;
      
      setForecastConfig(prev => ({
        ...prev,
        targetYear: defaultTargetYear,
        growthAbsolute: Math.round(avgYearTotal * 100) / 100
      }));
    }
  }, [show, goalData]);

  // Função para aplicar limites
  const applyLimits = (value) => {
    let result = value;
    if (forecastConfig.hasMinValue && result < forecastConfig.minValue) {
      result = forecastConfig.minValue;
    }
    if (forecastConfig.hasMaxValue && result > forecastConfig.maxValue) {
      result = forecastConfig.maxValue;
    }
    return result;
  };

  // Executar previsão
  const executeForecast = async () => {
    const { targetYear, growthPercent, growthType } = forecastConfig;
    
    if (selectedBaseYears.length === 0) {
      alert('Selecione pelo menos um ano base com resultados.');
      return;
    }
    
    if (!targetYear) {
      alert('Selecione o ano destino');
      return;
    }

    // Coletar valores históricos
    const monthlyBaseValues = {};
    let countHistorico = 0;
    let lastValue = null;
    
    goalData.matrix.forEach((row, monthIdx) => {
      const monthNum = monthIdx + 1;
      monthlyBaseValues[monthNum] = [];
      
      selectedBaseYears.forEach(year => {
        const cell = row.cells.find(c => c.year === year);
        if (cell && cell.result !== null && cell.result !== undefined && cell.status === 'concluded') {
          monthlyBaseValues[monthNum].push(cell.result);
          countHistorico++;
          lastValue = cell.result;
        }
      });
    });
    
    if (countHistorico === 0) {
      alert('Nenhum resultado concluído encontrado.');
      return;
    }
    
    // Calcular médias
    const avgMonthlyValues = {};
    let totalFromMonths = 0;
    let monthsWithData = 0;
    
    Object.keys(monthlyBaseValues).forEach(month => {
      const values = monthlyBaseValues[month];
      if (values.length > 0) {
        avgMonthlyValues[month] = values.reduce((a, b) => a + b, 0) / values.length;
        totalFromMonths += avgMonthlyValues[month];
        monthsWithData++;
      }
    });
    
    const avgMonthValue = monthsWithData > 0 ? totalFromMonths / monthsWithData : 0;
    const avgYearTotal = avgMonthValue * 12;
    
    const isAbsoluteMode = forecastConfig.growthMode === 'absolute';
    let targetYearTotal;
    let growthFactor;
    
    if (isAbsoluteMode) {
      targetYearTotal = forecastConfig.growthAbsolute;
      growthFactor = avgYearTotal > 0 ? targetYearTotal / avgYearTotal : 1;
    } else {
      growthFactor = 1 + growthPercent / 100;
      targetYearTotal = avgYearTotal * growthFactor;
    }
    
    const changesToSave = [];
    
    if (growthType === 'continuous') {
      // Valor inicial baseado no último valor conhecido ou média (sem aplicar growthFactor)
      const startValue = lastValue || avgMonthValue;
      const n = 12;
      // Calcular incremento mensal para que a soma seja igual ao targetYearTotal
      // Soma de progressão aritmética: S = n*a + n*(n-1)/2 * d
      // d = (S - n*a) / (n*(n-1)/2)
      const d = (targetYearTotal - n * startValue) / (n * (n - 1) / 2);
      
      goalData.matrix.forEach((row, monthIdx) => {
        const targetCell = row.cells.find(c => c.year === targetYear);
        if (targetCell) {
          // Valor cresce (ou decresce) ao longo dos meses
          const newTarget = startValue + (monthIdx * d);
          changesToSave.push({
            parent_goal_id: goalData.parent_goal_id || goalData.id,
            date: targetCell.date,
            target: Math.round(applyLimits(Math.max(0, newTarget)) * 100) / 100
          });
        }
      });
    } else if (growthType === 'seasonal') {
      goalData.matrix.forEach((row, monthIdx) => {
        const monthNum = monthIdx + 1;
        const avgBaseValue = avgMonthlyValues[monthNum] || avgMonthValue;
        const targetCell = row.cells.find(c => c.year === targetYear);
        
        if (targetCell) {
          const newTarget = avgBaseValue * growthFactor;
          changesToSave.push({
            parent_goal_id: goalData.parent_goal_id || goalData.id,
            date: targetCell.date,
            target: Math.round(applyLimits(Math.max(0, newTarget)) * 100) / 100
          });
        }
      });
    } else if (growthType === 'both') {
      // Valor inicial baseado no último valor conhecido ou média
      const startValue = lastValue || avgMonthValue;
      const n = 12;
      const d = (targetYearTotal - n * startValue) / (n * (n - 1) / 2);
      
      goalData.matrix.forEach((row, monthIdx) => {
        const monthNum = monthIdx + 1;
        const avgBaseValue = avgMonthlyValues[monthNum];
        const targetCell = row.cells.find(c => c.year === targetYear);
        
        if (targetCell) {
          // Componente contínuo: cresce/decresce linearmente
          const continuousValue = startValue + (monthIdx * d);
          // Componente sazonal: aplica o fator de crescimento ao padrão sazonal
          const seasonalValue = avgBaseValue ? avgBaseValue * growthFactor : continuousValue;
          // Média ponderada dos dois
          const newTarget = (continuousValue + seasonalValue) / 2;
          changesToSave.push({
            parent_goal_id: goalData.parent_goal_id || goalData.id,
            date: targetCell.date,
            target: Math.round(applyLimits(Math.max(0, newTarget)) * 100) / 100
          });
        }
      });
    }
    
    try {
      const response = await axios.post('/matrix/update', {
        matrix_data: changesToSave
      });
      
      if (response.data.success) {
        alert('Projeção aplicada com sucesso!');
        onForecastApplied && onForecastApplied(changesToSave, targetYear);
        onClose();
      }
    } catch (error) {
      console.error('Erro ao aplicar projeção:', error);
      alert('Erro ao aplicar projeção');
    }
  };

  if (!show || !goalData) return null;

  const years = goalData.years || [];

  return (
    <div 
      className="modal show d-block" 
      tabIndex="-1" 
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title">
              <i className="fas fa-chart-line me-2"></i>
              Projetar Metas - {goalData.title}
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            {/* Anos Base */}
            <div className="mb-3">
              <label className="form-label fw-bold">
                <i className="fas fa-calendar-alt me-1 text-secondary"></i>
                Anos Base (referência com resultados)
              </label>
              <div className="p-2 bg-light border rounded">
                {(() => {
                  const allYearsWithResults = getYearsWithResults(goalData);
                  return allYearsWithResults.length > 0 ? (
                    <div className="d-flex flex-wrap gap-2">
                      {allYearsWithResults.map(year => {
                        const isSelected = selectedBaseYears.includes(year);
                        const canDeselect = selectedBaseYears.length > 1;
                        return (
                          <span 
                            key={year} 
                            className={`badge ${isSelected ? 'bg-success' : 'bg-secondary'}`}
                            style={{ 
                              cursor: (isSelected && canDeselect) || !isSelected ? 'pointer' : 'not-allowed',
                              opacity: isSelected ? 1 : 0.6
                            }}
                            onClick={() => toggleBaseYear(year)}
                            title={isSelected 
                              ? (canDeselect ? 'Clique para remover este ano da base' : 'Não é possível remover o último ano') 
                              : 'Clique para adicionar este ano à base'}
                          >
                            <i className={`fas ${isSelected ? 'fa-check' : 'fa-plus'} me-1`}></i>{year}
                          </span>
                        );
                      })}
                    </div>
                  ) : (
                    <span className="text-muted">Nenhum ano com resultados concluídos</span>
                  );
                })()}
              </div>
              <small className="text-muted">Clique nos anos para incluir ou excluir da base de cálculo</small>
            </div>

            {/* Ano Destino */}
            <div className="mb-3">
              <label className="form-label fw-bold">
                <i className="fas fa-bullseye me-1 text-primary"></i>
                Ano Destino
              </label>
              <select 
                className="form-select"
                value={forecastConfig.targetYear || ''}
                onChange={(e) => setForecastConfig(prev => ({ ...prev, targetYear: parseInt(e.target.value) }))}
              >
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            {/* Tipo de Crescimento */}
            <div className="mb-3">
              <label className="form-label fw-bold">
                <i className="fas fa-chart-bar me-1 text-info"></i>
                Tipo de Crescimento
              </label>
              <div className="d-flex flex-column gap-2">
                {[
                  { value: 'continuous', label: 'Contínuo', desc: 'Crescimento uniforme mês a mês' },
                  { value: 'seasonal', label: 'Sazonal', desc: 'Baseado no mesmo mês do ano anterior' },
                  { value: 'both', label: 'Misto', desc: 'Combina contínuo + sazonalidade (50% cada)' }
                ].map(opt => (
                  <div 
                    key={opt.value}
                    className={`p-2 border rounded ${forecastConfig.growthType === opt.value ? 'border-primary bg-primary bg-opacity-10' : 'bg-white'}`}
                    onClick={() => setForecastConfig(prev => ({ ...prev, growthType: opt.value }))}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="d-flex align-items-center">
                      <input 
                        type="radio" 
                        className="form-check-input me-2" 
                        checked={forecastConfig.growthType === opt.value}
                        onChange={() => {}}
                      />
                      <div>
                        <strong className="text-dark">{opt.label}</strong>
                        <small className="d-block text-secondary">{opt.desc}</small>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Modo: Percentual ou Absoluto */}
            <div className="mb-3">
              <label className="form-label fw-bold">
                <i className="fas fa-calculator me-1 text-success"></i>
                Valor do Crescimento/Redução
              </label>
              <div className="btn-group w-100 mb-2">
                <button
                  type="button"
                  className={`btn ${forecastConfig.growthMode === 'percent' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setForecastConfig(prev => ({ ...prev, growthMode: 'percent' }))}
                >
                  <i className="fas fa-percent me-1"></i> Percentual
                </button>
                <button
                  type="button"
                  className={`btn ${forecastConfig.growthMode === 'absolute' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setForecastConfig(prev => ({ ...prev, growthMode: 'absolute' }))}
                >
                  <i className="fas fa-hashtag me-1"></i> Valor Absoluto
                </button>
              </div>
              
              {forecastConfig.growthMode === 'percent' ? (
                <div className="input-group">
                  <input 
                    type="number" 
                    className="form-control"
                    value={forecastConfig.growthPercent}
                    onChange={(e) => setForecastConfig(prev => ({ ...prev, growthPercent: parseFloat(e.target.value) || 0 }))}
                    step="0.5"
                  />
                  <span className="input-group-text">%</span>
                </div>
              ) : (
                <div className="input-group">
                  <input 
                    type="number" 
                    className="form-control"
                    value={forecastConfig.growthAbsolute}
                    onChange={(e) => setForecastConfig(prev => ({ ...prev, growthAbsolute: parseFloat(e.target.value) || 0 }))}
                  />
                  <span className="input-group-text">total anual</span>
                </div>
              )}
              <small className="text-secondary">
                {forecastConfig.growthMode === 'percent' 
                  ? 'Valores positivos = crescimento, negativos = redução'
                  : 'Total anual que deseja alcançar'}
              </small>
            </div>

            {/* Limites */}
            <div className="mb-3">
              <label className="form-label fw-bold">
                <i className="fas fa-sliders-h me-1 text-warning"></i>
                Limites de Valor
              </label>
              <div className="row g-2">
                <div className="col-6">
                  <div className="form-check mb-2">
                    <input 
                      type="checkbox" 
                      className="form-check-input" 
                      checked={forecastConfig.hasMinValue}
                      onChange={(e) => setForecastConfig(prev => ({ ...prev, hasMinValue: e.target.checked }))}
                    />
                    <label className="form-check-label text-dark">Mínimo</label>
                  </div>
                  <input 
                    type="number" 
                    className="form-control form-control-sm"
                    value={forecastConfig.minValue}
                    onChange={(e) => setForecastConfig(prev => ({ ...prev, minValue: parseFloat(e.target.value) || 0 }))}
                    disabled={!forecastConfig.hasMinValue}
                  />
                </div>
                <div className="col-6">
                  <div className="form-check mb-2">
                    <input 
                      type="checkbox" 
                      className="form-check-input" 
                      checked={forecastConfig.hasMaxValue}
                      onChange={(e) => setForecastConfig(prev => ({ ...prev, hasMaxValue: e.target.checked }))}
                    />
                    <label className="form-check-label text-dark">Máximo</label>
                  </div>
                  <input 
                    type="number" 
                    className="form-control form-control-sm"
                    value={forecastConfig.maxValue}
                    onChange={(e) => setForecastConfig(prev => ({ ...prev, maxValue: parseFloat(e.target.value) || 100 }))}
                    disabled={!forecastConfig.hasMaxValue}
                  />
                </div>
              </div>
            </div>

            {/* Prévia do cálculo */}
            {goalData && goalData.matrix && selectedBaseYears.length > 0 && (() => {
              // Calcular totais usando os anos selecionados
              let totalBase = 0;
              let totalMonthsWithData = 0;
              
              goalData.matrix.forEach(row => {
                selectedBaseYears.forEach(year => {
                  const cell = row.cells?.find(c => c.year === year);
                  if (cell && cell.result !== null && cell.result !== undefined && cell.status === 'concluded') {
                    totalBase += cell.result;
                    totalMonthsWithData++;
                  }
                });
              });
              
              if (totalMonthsWithData === 0) return null;
              
              const avgMonthBase = totalBase / totalMonthsWithData;
              const avgYearTotal = avgMonthBase * 12;
              const targetTotal = forecastConfig.growthMode === 'absolute'
                ? forecastConfig.growthAbsolute
                : avgYearTotal * (1 + forecastConfig.growthPercent / 100);
              const avgMonthTarget = targetTotal / 12;
              
              const avgMonthsPerYear = totalMonthsWithData / selectedBaseYears.length;
              const hasIncompleteData = avgMonthsPerYear < 12;
              
              const variation = targetTotal - avgYearTotal;
              const variationPercent = avgYearTotal > 0 ? ((targetTotal / avgYearTotal) - 1) * 100 : 0;
              
              const formatNum = (num) => num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
              
              return (
                <div className="mb-3">
                  <label className="form-label fw-bold">
                    <i className="fas fa-calculator me-1 text-info"></i>
                    Prévia do Cálculo
                  </label>
                  <div className="p-3 bg-white border rounded shadow-sm">
                    {hasIncompleteData && (
                      <div className="mb-2">
                        <span className="badge bg-warning text-dark">
                          <i className="fas fa-info-circle me-1"></i>
                          Extrapolado de {Math.round(avgMonthsPerYear)} meses
                        </span>
                      </div>
                    )}
                    <div className="row">
                      <div className="col-6">
                        <small className="d-block text-dark">
                          <i className="fas fa-calendar-alt me-1 text-secondary"></i>Total anual base{hasIncompleteData ? '*' : ''}:
                        </small>
                        <small className="d-block">
                          <strong className="text-primary">{formatNum(avgYearTotal)}</strong>
                        </small>
                      </div>
                      <div className="col-6">
                        <small className="d-block text-dark">
                          <i className="fas fa-chart-line me-1 text-secondary"></i>Média mensal base:
                        </small>
                        <small className="d-block">
                          <strong className="text-primary">{formatNum(avgMonthBase)}</strong>
                        </small>
                      </div>
                    </div>
                    <hr className="my-2" />
                    <div className="row">
                      <div className="col-6">
                        <small className="d-block text-dark">
                          <i className="fas fa-bullseye me-1 text-secondary"></i>Total destino ({forecastConfig.targetYear}):
                        </small>
                        <small className="d-block">
                          <strong className={variation >= 0 ? 'text-success' : 'text-danger'}>
                            {formatNum(targetTotal)}
                          </strong>
                          <span className={`badge ms-1 ${variation >= 0 ? 'bg-success' : 'bg-danger'}`} style={{ fontSize: '0.65rem' }}>
                            {variation >= 0 ? '+' : ''}{variationPercent.toFixed(1)}%
                          </span>
                        </small>
                      </div>
                      <div className="col-6">
                        <small className="d-block text-dark">
                          <i className="fas fa-chart-bar me-1 text-secondary"></i>Média mensal destino:
                        </small>
                        <small className="d-block">
                          <strong className={variation >= 0 ? 'text-success' : 'text-danger'}>
                            {formatNum(avgMonthTarget)}
                          </strong>
                        </small>
                      </div>
                    </div>
                    {hasIncompleteData && (
                      <small className="d-block text-secondary mt-2" style={{ fontSize: '0.7rem' }}>
                        * Total extrapolado para 12 meses baseado na média dos {Math.round(avgMonthsPerYear)} meses com dados
                      </small>
                    )}
                    {(forecastConfig.hasMinValue || forecastConfig.hasMaxValue) && (
                      <small className="d-block text-warning mt-1" style={{ fontSize: '0.7rem' }}>
                        <i className="fas fa-exclamation-triangle me-1"></i>
                        Limites aplicados: 
                        {forecastConfig.hasMinValue && ` mín: ${forecastConfig.minValue}`}
                        {forecastConfig.hasMinValue && forecastConfig.hasMaxValue && ' |'}
                        {forecastConfig.hasMaxValue && ` máx: ${forecastConfig.maxValue}`}
                      </small>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="button" className="btn btn-primary" onClick={executeForecast}>
              <i className="fas fa-magic me-1"></i>
              Aplicar Projeção
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForecastModal;
